// `toLocaleString(opts)` builds a new ICU formatter on every call (~20 µs);
// a cached `Intl.NumberFormat` formats the same value identically in well
// under 1 µs. The estimate calculations round every comp cost field, so the
// formatters are built once per option set and reused. ICU rounds half away
// from zero on the decimal value the number prints as — keep it doing the
// rounding rather than `Math.round`/`toFixed`, which disagree with it.
const numberFormats = new Map();

const getNumberFormat = (minimumFractionDigits, maximumFractionDigits) => {
  const key = `${minimumFractionDigits}:${maximumFractionDigits}`;
  if (!numberFormats.has(key)) {
    numberFormats.set(key, new Intl.NumberFormat('en-US', { minimumFractionDigits, maximumFractionDigits }));
  }
  return numberFormats.get(key);
};

export const formatNumber = (value = '', minDecimalPlaces = 0, maxDecimalPlaces = minDecimalPlaces) => {
  if (minDecimalPlaces > maxDecimalPlaces) {
    throw Error('Min decimal places must be more than max decimal places');
  }

  return getNumberFormat(minDecimalPlaces, maxDecimalPlaces).format(parseNumericValue(value));
};

const compactNumberFormat = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1
});

export const formatNumberToLabel = (value) => {
  return compactNumberFormat.format(parseNumericValue(value));
};

export const roundDollarValue = (value) => {
  return Number(getNumberFormat(2, 2).format(parseNumericValue(value)).replaceAll(',', ''));
};

export const safeDivide = (a, b) => {
  return b === 0 ? 0 : a / b;
};

export const applyPercentage = (value, percentage) => {
  const result = parseNumericValue(value) * (1 + parseNumericValue(percentage) / 100);
  // Round to 10 decimal places to avoid floating point precision issues
  return Number(result.toFixed(10));
};

export const parseNumericValue = (value) => {
  if (value === null || value === undefined) return 0;

  // If it's already a number, just return it
  if (typeof value === 'number') return value;

  // Convert to string and remove all characters except digits and decimal point
  const cleanedValue = value.toString().replace(/[^\d.-]/g, '');
  return parseFloat(cleanedValue) || 0;
};

// Same parse as parseNumericValue, but a value that does not parse comes back as
// undefined rather than as 0, so a caller can tell "no number here" from a
// legitimate zero. The dynamic tag rule evaluator needs that distinction — a
// garbage `value_1` has to fail its rule, and `parseNumericValue` would turn it
// into a `> 0` comparison that quietly passes.
//
// Mirrors `parseNumericValueOrReturnUndefined` in shiraz-api-v2
// (`app/helpers/numberHelpers.js`) exactly; the two evaluators are only equal if
// their number parsing is.
export const parseNumericValueOrReturnUndefined = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return value;
  const cleanedValue = value.toString().replace(/[^\d.-]/g, '');
  const parsedValue = parseFloat(cleanedValue);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

export const centsToDollars = (value) => {
  return safeDivide(parseNumericValue(value), 100);
};

export const isValidNumber = (input) => {
  if (typeof input !== 'string') return false;

  const s = input.trim();
  if (s.length === 0) return false;

  // handle sign
  let body = s;
  if (body[0] === '-') {
    body = body.slice(1);
    if (body.length === 0) return false; // just "-"
  }

  // split decimal
  const parts = body.split('.');
  if (parts.length > 2) return false;

  const intPart = parts[0];
  const fracPart = parts.length === 2 ? parts[1] : null;

  // integer (with optional commas)
  if (intPart.includes(',')) {
    if (intPart.startsWith(',') || intPart.endsWith(',') || intPart.includes(',,')) return false;

    const groups = intPart.split(',');

    // first group: 1–3 digits, no leading zeros if length > 1
    if (groups[0].length < 1 || groups[0].length > 3 || !/^\d+$/.test(groups[0])) return false;
    if (groups[0].length > 1 && groups[0][0] === '0') return false; // disallow "000,..." / "01,..."
    if (groups[0] === '0' && groups.length > 1) return false; // disallow "0,123"

    // subsequent groups: exactly 3 digits
    for (let i = 1; i < groups.length; i += 1) {
      if (groups[i].length !== 3 || !/^\d+$/.test(groups[i])) return false;
    }
  } else if (intPart !== '' && !/^\d+$/.test(intPart)) return false;

  // fractional (no commas)
  if (fracPart !== null) {
    if (fracPart === '') return false; // "12."
    if (fracPart.includes(',')) return false; // no commas after "."
    if (!/^\d+$/.test(fracPart)) return false;
  }

  return true;
};

// The largest magnitude a Decimal(10, 2) column holds — eight digits before the
// point, two after. The API rejects anything past it with a 400, so a form that
// writes one of those columns stops the value here, where it can say why.
export const DECIMAL_10_2_MAX = 99999999.99;
export const DECIMAL_10_2_MAX_MESSAGE = 'Must be less than 100,000,000';
